/*!
 * Copyright © 2021-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { isElementNull, isStringNullOrEmpty } from '../../../../utils';

import {
  EDisplayDriverAs,
  EDisplayVehicleAs,
  EPlotDirection,
  EPlotIcon,
  EPlotType,
  ESignalStatus,
  ESpeedLimitOption,
  ESpeedType,
  ESpeedUnits,
  ETrackableClassification,
  EVehicleStatusType,
  IAccountSettings,
  IEVBatteryData,
  ISpeedViolation,
  ISpeedViolationMessage,
  IUserSettings,
  IVehiclePlot,
  IVehiclePlotResponse,
  IVehiclePlotWebsubsResponse
} from '../../models';
import { PlotFormattingService } from '../plot-formatting/plot-formatting.service';
import { IPlotMappingService } from './plot-mapping.service.interface';

@Injectable({
  providedIn: 'root'
})
export class PlotMappingService implements IPlotMappingService {
  private static readonly _plotDirectionMap: EPlotDirection[] = [
    EPlotDirection.North,
    EPlotDirection.NorthEast,
    EPlotDirection.East,
    EPlotDirection.SouthEast,
    EPlotDirection.South,
    EPlotDirection.SouthWest,
    EPlotDirection.West,
    EPlotDirection.NorthWest,
    EPlotDirection.North
  ];

  private readonly _vehicleToPoweredAssetIconMapping = new Map<EPlotIcon, EPlotIcon>([
    [EPlotIcon.MovingN, EPlotIcon.MovingNPoweredAsset],
    [EPlotIcon.MovingNE, EPlotIcon.MovingNEPoweredAsset],
    [EPlotIcon.MovingE, EPlotIcon.MovingEPoweredAsset],
    [EPlotIcon.MovingSE, EPlotIcon.MovingSEPoweredAsset],
    [EPlotIcon.MovingS, EPlotIcon.MovingSPoweredAsset],
    [EPlotIcon.MovingSW, EPlotIcon.MovingSWPoweredAsset],
    [EPlotIcon.MovingW, EPlotIcon.MovingWPoweredAsset],
    [EPlotIcon.MovingNW, EPlotIcon.MovingNWPoweredAsset],
    [EPlotIcon.Idle, EPlotIcon.IdlePoweredAsset],
    [EPlotIcon.Stopped, EPlotIcon.StoppedPoweredAsset],
    [EPlotIcon.LostSignal, EPlotIcon.LostSignalPoweredAsset],
    [EPlotIcon.Towing, EPlotIcon.TowingPoweredAsset],
    [EPlotIcon.CustomerCare, EPlotIcon.CustomerCarePoweredAsset]
  ]);

  constructor(private readonly _plotFormattingService: PlotFormattingService) {}

  static mapPlotDirection(directionDegrees: number): EPlotDirection {
    const index = Math.floor((directionDegrees + 22.5) / 45);

    return this._plotDirectionMap[index];
  }

  static mapPlotDirectionToPlotIcon(direction: EPlotDirection, isPoweredAsset: boolean): EPlotIcon {
    switch (direction) {
      case EPlotDirection.North: {
        return isPoweredAsset ? EPlotIcon.MovingNPoweredAsset : EPlotIcon.MovingN;
      }
      case EPlotDirection.NorthEast: {
        return isPoweredAsset ? EPlotIcon.MovingNEPoweredAsset : EPlotIcon.MovingNE;
      }
      case EPlotDirection.East: {
        return isPoweredAsset ? EPlotIcon.MovingEPoweredAsset : EPlotIcon.MovingE;
      }
      case EPlotDirection.SouthEast: {
        return isPoweredAsset ? EPlotIcon.MovingSEPoweredAsset : EPlotIcon.MovingSE;
      }
      case EPlotDirection.South: {
        return isPoweredAsset ? EPlotIcon.MovingSPoweredAsset : EPlotIcon.MovingS;
      }
      case EPlotDirection.SouthWest: {
        return isPoweredAsset ? EPlotIcon.MovingSWPoweredAsset : EPlotIcon.MovingSW;
      }
      case EPlotDirection.West: {
        return isPoweredAsset ? EPlotIcon.MovingWPoweredAsset : EPlotIcon.MovingW;
      }
      case EPlotDirection.NorthWest: {
        return isPoweredAsset ? EPlotIcon.MovingNWPoweredAsset : EPlotIcon.MovingNW;
      }

      default: {
        return isPoweredAsset ? EPlotIcon.MovingNPoweredAsset : EPlotIcon.MovingN;
      }
    }
  }

  public mapVehiclePlotsResponseToStateModel(
    response: IVehiclePlotResponse[],
    searchLightVehicleIds: number[],
    classificationsFromHierachy: Map<string, ETrackableClassification>,
    userSettings: IUserSettings,
    accountSettings: IAccountSettings,
    isTowingFeatureEnabled: boolean,
    existingVehiclePlots?: IVehiclePlot[]
  ): IVehiclePlot[] {
    let vehiclePlots: IVehiclePlot[] = [];
    const existingPlotsById = new Map<number, IVehiclePlot>();
    if (existingVehiclePlots && existingVehiclePlots.length > 0) {
      existingVehiclePlots.forEach(existingvehiclePlot => existingPlotsById.set(existingvehiclePlot.id, existingvehiclePlot));
    }
    response.forEach((vehicle: IVehiclePlotResponse) => {
      const isFromSearchLight = searchLightVehicleIds.includes(vehicle.id);
      vehiclePlots = vehiclePlots.concat(
        this.convertVehiclePlotModels(
          [vehicle],
          isFromSearchLight,
          classificationsFromHierachy,
          userSettings,
          accountSettings,
          isTowingFeatureEnabled,
          existingPlotsById
        )
      );
    });
    return vehiclePlots;
  }

  public convertVehiclePlotModels(
    vehiclePlots: (IVehiclePlotResponse | IVehiclePlotWebsubsResponse)[],
    isFromSearchLight = false,
    classificationsFromHierachy: Map<string, ETrackableClassification>,
    userSettings: IUserSettings,
    accountSettings: IAccountSettings,
    isTowingFeatureEnabled: boolean,
    existingVehiclePlotsById?: Map<number, IVehiclePlot>
  ): IVehiclePlot[] {
    return vehiclePlots.map(vehiclePlot =>
      this._mapVehiclePlotResponseToModel(
        vehiclePlot,
        userSettings,
        accountSettings,
        isFromSearchLight,
        Date.now(),
        classificationsFromHierachy,
        isTowingFeatureEnabled,
        existingVehiclePlotsById?.get(vehiclePlot.id)
      )
    );
  }

  public mapSpeedViolationMessageToSpeedViolation(
    vehicleUpdateMessage: ISpeedViolationMessage,
    accountSettings: IAccountSettings,
    userSettings: IUserSettings
  ): ISpeedViolation {
    // if the message is coming from websubs, then we need to calculate the speed violation

    return this._shouldCalculateSpeedViolation(vehicleUpdateMessage)
      ? this._calculateAndLocalizeSpeedViolation({
          data: {
            actualSpeed: vehicleUpdateMessage.speed,
            isPostedSpeedVerified: vehicleUpdateMessage.isPostedSpeedVerified,
            postedSpeedLimit: vehicleUpdateMessage.postedSpeed,
            referenceSpeed: vehicleUpdateMessage.referenceSpeed
          },
          userOrAccountSettings: {
            speedLimitOption: accountSettings.SpeedLimitOption,
            speedUnits: userSettings.SpeedUnits,
            verifiedPostedSpeedsOnly: accountSettings.VerifiedPostedSpeedsOnly
          }
        })
      : {
          inrixSpeedLimitType: vehicleUpdateMessage.InrixSpeedLimitType,
          violationSpeed: vehicleUpdateMessage.ViolationSpeed
        };
  }

  private _shouldCalculateSpeedViolation(vehicleToUpdate: ISpeedViolationMessage): boolean {
    // if it is the new contract, then ViolationSpeed is obsolete and the raw data is present
    return isElementNull(vehicleToUpdate.ViolationSpeed) && !isElementNull(vehicleToUpdate.speed);
  }

  private _calculateAndLocalizeSpeedViolation(params: ISpeedViolationCalculationParams): ISpeedViolation {
    const speedViolation = this._getSpeedLimitForSpeedViolation(params);

    if (speedViolation.violationSpeed > 0) {
      speedViolation.violationSpeed = this._getLocalizedSpeedViolation(
        params.data.actualSpeed,
        speedViolation.violationSpeed,
        params.userOrAccountSettings.speedUnits
      );
    }

    return speedViolation;
  }
  private _getLocalizedSpeedViolation(actualSpeedKm: number, speedLimitKm: number, speedUnits: ESpeedUnits): number {
    const speedLimitLocale = this._plotFormattingService.formatSpeed(speedLimitKm, speedUnits);
    const actualSpeedLocale = this._plotFormattingService.formatSpeed(actualSpeedKm, speedUnits);

    if (speedLimitLocale > 0) {
      const isOverSpeedLimit = actualSpeedLocale > speedLimitLocale;
      if (isOverSpeedLimit) {
        return speedLimitLocale;
      }
    }

    return 0;
  }

  private _getSpeedLimitForSpeedViolation(params: ISpeedViolationCalculationParams): ISpeedViolation {
    let violationSpeedLimitType = ESpeedType.None;
    let speedLimit;

    // enum can have multiple values - check if IncludeRoadSpeed is set
    /* eslint-disable no-bitwise */
    const shouldIncludeRoadSpeed =
      (params.userOrAccountSettings.speedLimitOption & ESpeedLimitOption.IncludeRoadSpeed) === ESpeedLimitOption.IncludeRoadSpeed;
    const shouldIncludePostedSpeed =
      (params.userOrAccountSettings.speedLimitOption & ESpeedLimitOption.IncludePostedSpeed) === ESpeedLimitOption.IncludePostedSpeed;
    /* eslint-disable no-bitwise */
    const shouldIncludeBoth = shouldIncludeRoadSpeed && shouldIncludePostedSpeed;

    const postedSpeedCondition1 =
      shouldIncludePostedSpeed &&
      params.userOrAccountSettings.verifiedPostedSpeedsOnly &&
      params.data.isPostedSpeedVerified &&
      !isElementNull(params.data.postedSpeedLimit);
    const postedSpeedCondition2 =
      shouldIncludePostedSpeed && !params.userOrAccountSettings.verifiedPostedSpeedsOnly && !isElementNull(params.data.postedSpeedLimit);

    const roadSpeedCondition1 =
      shouldIncludeBoth &&
      !params.userOrAccountSettings.verifiedPostedSpeedsOnly &&
      isElementNull(params.data.actualSpeed) &&
      !isElementNull(params.data.referenceSpeed);
    const roadSpeedCondition2 = shouldIncludeRoadSpeed && !isElementNull(params.data.referenceSpeed);

    if (postedSpeedCondition1 || postedSpeedCondition2) {
      speedLimit = params.data.postedSpeedLimit;
      violationSpeedLimitType = ESpeedType.PostedSpeed;
    } else if (roadSpeedCondition1 || roadSpeedCondition2) {
      speedLimit = params.data.referenceSpeed;
      violationSpeedLimitType = ESpeedType.RoadSpeed;
    }

    return {
      inrixSpeedLimitType: violationSpeedLimitType,
      violationSpeed: speedLimit
    };
  }

  private _mapVehiclePlotResponseToModel(
    vehiclePlot: IVehiclePlotResponse | IVehiclePlotWebsubsResponse,
    userSettings: IUserSettings,
    accountSettings: IAccountSettings,
    isFromSearchLight: boolean,
    batchTimestampTicks: number,
    classificationsFromHierachy: Map<string, ETrackableClassification>,
    isTowingFeatureEnabled: boolean,
    existingVehiclePlot?: IVehiclePlot
  ) {
    // map the propperties of IVehiclePlotResponse
    const vehiclePlotModel: IVehiclePlot = this._mapVehiclePlotResponsetoModelBase(
      vehiclePlot,
      userSettings,
      isFromSearchLight,
      batchTimestampTicks,
      classificationsFromHierachy,
      isTowingFeatureEnabled,
      existingVehiclePlot
    );

    const isIVehiclePlotWebsubsResponse = !isElementNull((vehiclePlot as IVehiclePlotWebsubsResponse).driverFirstName);
    if (isIVehiclePlotWebsubsResponse) {
      // map the properties that are not localized:
      this._mapAndLocalizeWebsubsOnlyFields(
        vehiclePlotModel,
        vehiclePlot as IVehiclePlotWebsubsResponse,
        userSettings,
        accountSettings,
        existingVehiclePlot
      );
    }

    return vehiclePlotModel;
  }

  private _mapAndLocalizeWebsubsOnlyFields(
    vehiclePlotModel: IVehiclePlot,
    vehiclePlot: IVehiclePlotWebsubsResponse,
    userSettings: IUserSettings,
    accountSettings: IAccountSettings,
    existingVehiclePlot?: IVehiclePlot
  ): void {
    // check if the message timestamp is from today, in the user local timezone
    vehiclePlotModel.lastUpdateIsToday = this._plotFormattingService.getLastUpdateIsToday(
      vehiclePlot.ticks,
      userSettings.TimeZoneAndroidId
    );

    // format the driver name based on user / account settings
    vehiclePlotModel.driverDisplayName = this._plotFormattingService.formatDriverName(
      vehiclePlot,
      // prefer user settings but default to account settings
      userSettings.DisplayDriverAs !== EDisplayDriverAs.NotSet ? userSettings.DisplayDriverAs : accountSettings.DisplayDriverAs
    );

    // format the vehicle name based on user / account settings
    vehiclePlotModel.vehicleName = this._plotFormattingService.formatVehicleName(
      vehiclePlot,
      // prefer user settings but default to account settings
      userSettings.DisplayVehicleAs !== EDisplayVehicleAs.NotSet ? userSettings.DisplayVehicleAs : accountSettings.DisplayVehicleAs
    );

    // format the speed based on user settings
    vehiclePlotModel.speed = this._plotFormattingService.formatSpeed(vehiclePlot.spd, userSettings.SpeedUnits);

    // speed violation no longer comes from IVehiclePlotWebsubsResponse so we need to take it from the store if it exists
    vehiclePlotModel.speedViolation = existingVehiclePlot?.speedViolation;
    vehiclePlotModel.vehicleSpeedlimit = existingVehiclePlot?.vehicleSpeedlimit;
    vehiclePlotModel.vehicleSpeedlimitType = existingVehiclePlot?.vehicleSpeedlimitType;
  }

  private _mapVehiclePlotResponsetoModelBase(
    vehiclePlot: IVehiclePlotResponse | IVehiclePlotWebsubsResponse,
    userSettings: IUserSettings,
    isFromSearchLight: boolean,
    batchTimestampTicks: number,
    classificationsFromHierachy: Map<string, ETrackableClassification>,
    isTowingFeatureEnabled: boolean,
    existingVehiclePlot?: IVehiclePlot
  ) {
    const vehiclePlotModel: IVehiclePlot = {
      id: vehiclePlot.id,
      coordinates: {
        lat: vehiclePlot.Coordinate.Latitude,
        lng: vehiclePlot.Coordinate.Longitude
      },
      geofenceName: vehiclePlot.GeofenceName,
      signalStatus: {
        iconClass: vehiclePlot.icncls === EPlotIcon.LostSignal ? ESignalStatus.Lost : ESignalStatus.Normal,
        lastUpdate: null
      },
      lastUpdateForDisplay: this._plotFormattingService.formatLastUpdateForDisplay(vehiclePlot.ticks, userSettings),
      lastUpdateIsToday: vehiclePlot.LastUpdateIsToday,
      status: this._mapVehiclePlotStatus(vehiclePlot),
      // set address to null to differentiate "received a plot with no address"
      // and "searched for address and no address found for coordinates"
      address: isStringNullOrEmpty(vehiclePlot.ads) ? null : vehiclePlot.ads,
      addressId: vehiclePlot.adsId,
      direction: vehiclePlot.dir,
      driverId: vehiclePlot.DID,
      driverDisplayName: vehiclePlot.dnme,
      duration: vehiclePlot.dur >= 0 ? vehiclePlot.dur : 0,
      eventCode: vehiclePlot.evcd,
      group: vehiclePlot.grp,
      iconClass: vehiclePlot.icncls,
      offsensors: vehiclePlot.offsensors,
      onsensors: vehiclePlot.onsensors,
      speed: vehiclePlot.spd,
      speedViolation: {
        inrixSpeedLimitType: vehiclePlot.spdvio ? vehiclePlot.spdvio.InrixSpeedLimitType : null,
        violationSpeed: vehiclePlot.spdvio ? vehiclePlot.spdvio.ViolationSpeed : null
      },
      ticks: vehiclePlot.ticks,
      time: vehiclePlot.tme,
      timeUnits: vehiclePlot.tmeu,
      vehicleName: vehiclePlot.vnme,
      vehicleSpeedlimit: vehiclePlot.vspdlmt,
      vehicleSpeedlimitType: vehiclePlot.vspdlmttype,
      hasNavigationDevice: vehiclePlot.HasNavigationDevice,
      isFromSearchLight,
      isUpdating: false,
      batchTimestampTicks,
      type: EPlotType.vehicle,
      evBatteryData: this._getEVBatteryData(vehiclePlot, existingVehiclePlot)
    };

    if (vehiclePlot.TrackableClassification === ETrackableClassification.NotFound) {
      // If value is missing, use hierarchy value
      const hierarchyValue = classificationsFromHierachy.get(`v${vehiclePlot.id}`);
      vehiclePlot.TrackableClassification = hierarchyValue ?? ETrackableClassification.Vehicle;
    }

    vehiclePlotModel.iconClass = this._getIconClass(vehiclePlot, isTowingFeatureEnabled);
    vehiclePlotModel.type = this._convertTrackableClassificationToPlotType(vehiclePlot.TrackableClassification);

    this._setEVChargingDuration(vehiclePlotModel, vehiclePlot);
    return vehiclePlotModel;
  }
  private _setEVChargingDuration(vehiclePlotModel: IVehiclePlot, vehiclePlot: IVehiclePlotResponse | IVehiclePlotWebsubsResponse): void {
    if (this._isEVBatteryChargingAndNotFull(vehiclePlot)) {
      vehiclePlotModel.duration = vehiclePlotModel.evBatteryData?.lastChargingStartTime
        ? Math.floor(Date.now() / 1000) - vehiclePlotModel.evBatteryData?.lastChargingStartTime?.getTime() / 1000
        : null;
    }
  }

  private _getEVBatteryData(
    vehiclePlot: IVehiclePlotResponse | IVehiclePlotWebsubsResponse,
    existingVehiclePlot?: IVehiclePlot
  ): IEVBatteryData {
    if (vehiclePlot.Status === EVehicleStatusType.Private && existingVehiclePlot && existingVehiclePlot.evBatteryData) {
      return existingVehiclePlot.evBatteryData;
    }

    if (this._isVehiclePlotWebSubsResponse(vehiclePlot)) {
      return !isElementNull(vehiclePlot?.lastKnownTractionBatteryChargeLevel)
        ? {
            batteryLastUpdateTime: vehiclePlot.lastKnownTractionBatteryChargeLevelTimestamp
              ? new Date(vehiclePlot.lastKnownTractionBatteryChargeLevelTimestamp)
              : null,
            batteryPercentage: vehiclePlot.lastKnownTractionBatteryChargeLevel ?? null,
            lastChargingStartTime: vehiclePlot.lastKnownTractionBatteryChargingStartTimestamp
              ? new Date(vehiclePlot.lastKnownTractionBatteryChargingStartTimestamp)
              : null,
            chargingState: vehiclePlot.tractionBatteryCharging ?? null
          }
        : null;
    } else {
      return vehiclePlot?.EVBatteryData
        ? {
            batteryLastUpdateTime: vehiclePlot.EVBatteryData?.BatteryLastUpdateTime
              ? new Date(vehiclePlot.EVBatteryData.BatteryLastUpdateTime)
              : null,
            batteryPercentage: vehiclePlot.EVBatteryData?.BatteryPercentage ?? null,
            lastChargingStartTime: vehiclePlot.EVBatteryData?.LastChargingStartTime
              ? new Date(vehiclePlot.EVBatteryData.LastChargingStartTime)
              : null,
            chargingState: vehiclePlot.EVBatteryData?.ChargingState ?? null
          }
        : null;
    }
  }

  private _mapVehiclePlotStatus(vehiclePlot: IVehiclePlotResponse | IVehiclePlotWebsubsResponse): EVehicleStatusType {
    if (this._shouldOverrideChargingStatus(vehiclePlot)) {
      return vehiclePlot.Status as EVehicleStatusType;
    }

    return this._isEVBatteryChargingAndNotFull(vehiclePlot) ? EVehicleStatusType.Charging : (vehiclePlot.Status as EVehicleStatusType);
  }

  private _getIconClass(vehiclePlot: IVehiclePlotResponse | IVehiclePlotWebsubsResponse, isTowingFeatureEnabled: boolean): EPlotIcon {
    if (this._shouldOverrideChargingStatus(vehiclePlot)) {
      return vehiclePlot.icncls;
    }

    const isVehicle =
      !isElementNull(vehiclePlot.TrackableClassification) && vehiclePlot.TrackableClassification === ETrackableClassification.Vehicle;

    if (isVehicle) {
      return this._isEVBatteryChargingAndNotFull(vehiclePlot) ? EPlotIcon.Charging : vehiclePlot.icncls;
    }

    const isPoweredAsset =
      !isElementNull(vehiclePlot.TrackableClassification) && vehiclePlot.TrackableClassification === ETrackableClassification.PoweredAsset;

    // if towing is feature is disabled, and we get a towing plot from the backend, convert it to a moving
    // we need to map the direction (0-360 degrees) to the correct icon (north, north-east etc... and vehicle/powered asset)

    if (!isTowingFeatureEnabled && vehiclePlot.icncls === EPlotIcon.Towing) {
      const direction = PlotMappingService.mapPlotDirection(vehiclePlot.dir);
      const icon = PlotMappingService.mapPlotDirectionToPlotIcon(direction, isPoweredAsset);
      return icon;
    }

    if (isPoweredAsset) {
      if (this._vehicleToPoweredAssetIconMapping.has(vehiclePlot.icncls)) {
        return this._vehicleToPoweredAssetIconMapping.get(vehiclePlot.icncls);
      }
    }

    return vehiclePlot.icncls;
  }

  private _convertTrackableClassificationToPlotType(trackableClassification: ETrackableClassification): EPlotType {
    return trackableClassification === ETrackableClassification.PoweredAsset ? EPlotType.poweredAsset : EPlotType.vehicle;
  }

  private _isEVBatteryChargingAndNotFull(vehiclePlot: IVehiclePlotResponse | IVehiclePlotWebsubsResponse): boolean {
    return this._isVehiclePlotWebSubsResponse(vehiclePlot)
      ? vehiclePlot?.tractionBatteryCharging && vehiclePlot?.lastKnownTractionBatteryChargeLevel < 100
      : vehiclePlot?.EVBatteryData?.ChargingState && vehiclePlot?.EVBatteryData?.BatteryPercentage < 100;
  }

  private _isVehiclePlotWebSubsResponse(
    vehiclePlot: IVehiclePlotResponse | IVehiclePlotWebsubsResponse
  ): vehiclePlot is IVehiclePlotWebsubsResponse {
    return vehiclePlot && (vehiclePlot as IVehiclePlotWebsubsResponse).label !== undefined;
  }

  private _shouldOverrideChargingStatus(vehiclePlot: IVehiclePlotResponse | IVehiclePlotWebsubsResponse) {
    return [EVehicleStatusType.Panic, EVehicleStatusType.Private].includes(vehiclePlot.Status);
  }
}

interface ISpeedViolationCalculationParams {
  userOrAccountSettings: {
    speedLimitOption: ESpeedLimitOption;
    verifiedPostedSpeedsOnly: boolean;
    speedUnits: ESpeedUnits;
  };
  data: {
    isPostedSpeedVerified: boolean;
    actualSpeed: number;
    postedSpeedLimit: number;
    referenceSpeed: number;
  };
}
